/**
 * CampaignCards - Grid of campaign cards with QR codes and actions
 */
import { Download, Eye, ExternalLink, Edit2, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/components/admin/shared/constants';
import { getThemeByKey, PRODUCTION_DOMAIN } from './campaignUtils';

export default function CampaignCards({ 
  report, 
  allCampaigns, 
  schedules,
  onViewSignups,
  onEditCampaign,
  onToggleCampaign,
  onDownloadQR
}) {
  const copyCampaignUrl = (campaignId) => {
    const url = `${PRODUCTION_DOMAIN}/join/${campaignId}`;
    navigator.clipboard.writeText(url);
    toast.success('Campaign URL copied!');
  };

  // Merge allCampaigns with campaigns from report to include any new signups-only campaigns
  const getMergedCampaigns = () => {
    const campaignIds = new Set();
    const mergedCampaigns = [];
    
    // Add all campaigns from allCampaigns (dynamic, not hardcoded)
    allCampaigns.forEach(c => {
      campaignIds.add(c.campaign_id);
      mergedCampaigns.push(c);
    });
    
    // Add any campaigns from report that aren't already in the list
    report?.campaigns?.forEach(c => {
      if (!campaignIds.has(c.campaign_id)) {
        campaignIds.add(c.campaign_id);
        mergedCampaigns.push({
          campaign_id: c.campaign_id,
          title: c.title,
          theme: c.theme || 'teal',
          is_enabled: true,
          signup_count: c.signups
        });
      }
    });
    
    return mergedCampaigns.sort((a, b) => a.campaign_id - b.campaign_id);
  };

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {getMergedCampaigns().map(campaignData => {
        const id = campaignData.campaign_id;
        const campaign = report?.campaigns?.find(c => c.campaign_id === id);
        const themeKey = campaignData.theme || campaign?.theme || 'teal';
        const themeOption = getThemeByKey(themeKey);
        const theme = {
          gradient: themeOption.gradient,
          color: themeOption.textColor,
          bg: themeOption.bg,
          border: themeOption.border
        };
        const isLeading = report?.leading_campaign === id;
        const schedule = schedules.find(s => s.campaign_id === id);
        const isEnabled = campaignData.is_enabled !== false;
        const isNewCampaign = id > 4;
      
        return (
          <div 
            key={id}
            className={`rounded-2xl overflow-hidden border-2 transition-all ${
              !isEnabled ? 'opacity-60' : ''
            } ${isLeading ? theme.border : 'border-border'} ${theme.bg}`}
            data-testid={`campaign-card-${id}`}
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${theme.gradient} p-4 text-white`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium opacity-90">Campaign {id}</span>
                <div className="flex gap-2">
                  {isLeading && (
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">Leading</span>
                  )}
                  {!isEnabled && (
                    <span className="bg-red-500/50 px-2 py-0.5 rounded-full text-xs">Disabled</span>
                  )}
                  {schedule?.is_active === false && (
                    <span className="bg-yellow-500/50 px-2 py-0.5 rounded-full text-xs">Paused</span>
                  )}
                  {isNewCampaign && (
                    <span className="bg-green-500/50 px-2 py-0.5 rounded-full text-xs">New</span>
                  )}
                </div>
              </div>
              <p className="font-medium text-sm line-clamp-2">
                {campaignData.title || campaign?.title || `Campaign ${id}`}
              </p>
            </div>

            {/* Stats */}
            <div className="p-6">
              <div className="text-center mb-6">
                <p className={`text-5xl font-bold ${theme.color}`}>
                  {campaign?.signups || campaignData.signup_count || 0}
                </p>
                <p className="text-sm text-muted-foreground">Signups</p>
                {campaignData.discount_percent !== undefined && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {campaignData.discount_percent}% discount
                  </p>
                )}
              </div>

              {/* QR Code Preview */}
              <div className="bg-white dark:bg-gray-100 rounded-xl p-4 mb-4 flex justify-center">
                <img 
                  src={`${API}/api/campaigns/${id}/qr?size=120`}
                  alt={`Campaign ${id} QR`}
                  className="w-24 h-24"
                />
              </div>

              {/* Actions Row 1 */}
              <div className="flex gap-2 mb-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => onDownloadQR(id)}
                  title="Download QR Code"
                >
                  <Download className="w-4 h-4 mr-1" />
                  QR
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => onViewSignups(id)}
                  title="View Signups"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => window.open(`/join/${id}`, '_blank')}
                  title="Open Campaign Page"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Actions Row 2 - Edit & Toggle */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => onEditCampaign(campaignData)}
                  title="Edit Campaign"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => onToggleCampaign(id)}
                  title={isEnabled ? 'Disable Campaign' : 'Enable Campaign'}
                >
                  {isEnabled ? (
                    <>
                      <ToggleRight className="w-4 h-4 mr-1 text-green-500" />
                      On
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-4 h-4 mr-1 text-gray-400" />
                      Off
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="rounded-full"
                  onClick={() => copyCampaignUrl(id)}
                  title="Copy Campaign URL"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
